import db, { firebase } from "../Firebase";

const TYPE_PREFIX = {
  coupon: "CPN",
  offer: "OFR",
  wallet_cashback: "CSH",
  referral: "REF",
};

const STACK_GROUP = {
  coupon: "discount",
  offer: "discount",
  wallet_cashback: "cashback",
  referral: "referral",
};

const EMPTY_STATS = {
  customersUsed: 0,
  subscriptions: 0,
  rechargeAmt: 0,
  discountGiven: 0,
  creditGiven: 0,
  revenue: 0,
  referralsCreated: 0,
  referralsSuccessful: 0,
};

const formatId = (prefix, n) => `WF-${prefix}-${String(n).padStart(4, "0")}`;


const CODE_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
const CODE_TAKEN = "CODE_TAKEN";

const generateCouponCode = (length = 8) => {
  const bytes = new Uint32Array(length);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => CODE_CHARS[b % CODE_CHARS.length]).join("");
};

const toTimestamp = (dateStr) =>
  dateStr ? firebase.firestore.Timestamp.fromDate(new Date(dateStr)) : null;

// productIds array if given, otherwise fall back to the single productId (older data)
const idsOf = (obj = {}) =>
  Array.isArray(obj.productIds) && obj.productIds.length
    ? obj.productIds
    : obj.productId
      ? [obj.productId]
      : [];


const buildConfig = (type, config = {}) => {
  if (type === "coupon") {
    return {
      applyMode: config.applyMode || "code", // "code" (customer enters code) | "crm" (applied from CRM) | "both"
      code: (config.code || "").toUpperCase(), // final code yaha fill hoga (createCampaign ke andar)
      allowAgentApply: !!config.allowAgentApply, // true when applyMode is "crm" or "both"
      discountType: config.discountType || "percent", // "fixed" | "percent"
      discountValue: Number(config.discountValue || 0),
      discountAmount: config.discountAmount ?? null, // calculated by applyPricing
      finalPrice: config.finalPrice ?? null, // calculated by applyPricing
    };
  }
  if (type === "offer") {
    return {
      condition: {
        kind: config.condition?.kind || "active_sub", // "active_sub" | "subscribed_before" | "tenure_months"
        productId: config.condition?.productId || "", // first eligible product (kept for older readers)
        productIds: idsOf(config.condition), // ALL eligible products (any one of them satisfies the condition)
        items: Array.isArray(config.condition?.items) ? config.condition.items : [], // required quantity per product: [{ productId, packaging, unit, packs, value }]
        value: config.condition?.value ?? null,
        packaging: config.condition?.packaging ?? null, // pack text e.g. "1 L", "500 g"
        unit: config.condition?.unit || "", // "L", "ml", "g", "kg"...
        packs: config.condition?.packs ?? null, // number of packs entered
      },
      benefit: {
        kind: config.benefit?.kind || "free_product", // "free_product" | "special_price" | "wallet_credit"
        productId: config.benefit?.productId || "",
        productIds: idsOf(config.benefit), // special price: every eligible product; free product: the one free product
        qty: config.benefit?.qty ?? null,
        packaging: config.benefit?.packaging ?? null,
        unit: config.benefit?.unit || "",
        packs: config.benefit?.packs ?? null,
        amount: config.benefit?.amount ?? null,
        discountAmount: config.benefit?.discountAmount ?? null, // calculated by applyPricing
        finalPrice: config.benefit?.finalPrice ?? null, // calculated by applyPricing
      },
      frequency: config.frequency ? { every: Number(config.frequency.every || 1), unit: config.frequency.unit || "month" } : null,
    };
  }
  if (type === "wallet_cashback") {
    return {
      minRecharge: Number(config.minRecharge || 0),
      creditAmount: Number(config.creditAmount || 0),
      creditExpiryDays: config.creditExpiryDays ?? null,
    };
  }
  // referral
  return {
    referrerReward: Number(config.referrerReward || 0),
    refereeReward: Number(config.refereeReward || 0),
    minRecharge: Number(config.minRecharge || 0),
    subscriptionRequired: !!config.subscriptionRequired,
    eligibleProductIds: config.eligibleProductIds || [],
    maxSuccessfulPerReferrer: config.maxSuccessfulPerReferrer ?? null,
  };
};

// ---------------------------------------------------------
// PRICING: discountAmount + finalPrice ek hi jagah calculate hote hain
// (create aur edit dono yahi use karte hain)
// ---------------------------------------------------------
const roundMoney = (n) => Math.round(n * 100) / 100;

// product ki normal price (products_data se)
const priceOfProduct = async (productId) => {
  if (!productId) return null;
  const products = await getProducts();
  const p = products.find((x) => String(x.id) === String(productId));
  return p?.price ?? null;
};

// coupon: discountType + discountValue + price -> discountAmount, finalPrice
const calcCouponPricing = (config = {}, price) => {
  const value = Number(config.discountValue || 0);
  if (price == null) {
    console.warn("Product price not found, finalPrice cleared");
    return {
      discountAmount: config.discountType === "fixed" ? value : null,
      finalPrice: null,
    };
  }
  const raw = config.discountType === "percent" ? (price * value) / 100 : value;
  const discountAmount = Math.min(Math.max(raw, 0), price); // price se zyada discount nahi
  return {
    discountAmount: roundMoney(discountAmount),
    finalPrice: roundMoney(price - discountAmount),
  };
};

// offer (special_price): amount hi final price hai
const calcSpecialPricing = (amount, price) => {
  const final = Number(amount || 0);
  return {
    finalPrice: roundMoney(final),
    discountAmount: price == null ? null : roundMoney(Math.max(price - final, 0)),
  };
};

// config me discountAmount / finalPrice bhar ke wapas deta hai
const applyPricing = async (type, config = {}, productIds = []) => {
  if (type === "coupon") {
    const price = await priceOfProduct(productIds[0]);
    return { ...config, ...calcCouponPricing(config, price) };
  }
  if (type === "offer") {
    const b = config.benefit || {};
    if (b.kind === "special_price") {
      const price = await priceOfProduct(b.productId || productIds[0]);
      return { ...config, benefit: { ...b, ...calcSpecialPricing(b.amount, price) } };
    }
    // free product: discount / final price lagu nahi hota
    return { ...config, benefit: { ...b, discountAmount: null, finalPrice: null } };
  }
  return config;
};

// ---------------------------------------------------------

// ---------------------------------------------------------
export const previewNextCampaignId = async (type) => {
  const prefix = TYPE_PREFIX[type];
  if (!prefix) return "";

  const doc = await db.collection("counters").doc(`campaign_${prefix}`).get();
  const last = doc.exists ? doc.data().lastId : 0;
  return formatId(prefix, last + 1);
};

// ---------------------------------------------------------
// 
// ---------------------------------------------------------
export const createCampaign = async (input) => {
  const { activateNow, type, name, description, startDate, endDate, maxUsesPerCustomer, eligibility = {}, config = {} } = input;

  const prefix = TYPE_PREFIX[type];
  if (!prefix) {
    throw new Error("Ye campaign type abhi supported nahi hai.");
  }
  if (!name || !name.trim()) {
    throw new Error("Campaign ka naam dena zaroori hai.");
  }

 
  if (type === "referral") {
    const existing = await db.collection("campaigns").where("type", "==", "referral").limit(1).get();
    if (!existing.empty) {
      throw new Error(
        "A Refer & Earn campaign already exists. You cannot create a new one; you can edit, activate or deactivate the existing campaign."
      );
    }
  }

  const isCoupon = type === "coupon";
  const adminGivenCode = isCoupon && config.code ? config.code.trim().toUpperCase() : "";
  const counterRef = db.collection("counters").doc(`campaign_${prefix}`);

  // price lookup transaction ke bahar hona chahiye, isliye pehle hi nikal lo
  const pricedConfig = await applyPricing(type, config, eligibility.productIds || []);


  const attemptSave = async (code) => {
    let campaignCode = "";
    const codeRef = code ? db.collection("couponCodes").doc(code) : null;
    let campaignRef = null;

    await db.runTransaction(async (transaction) => {
      // ---------- PEHLE SAB PADHO ----------
      const counterDoc = await transaction.get(counterRef);
      if (codeRef) {
        const codeDoc = await transaction.get(codeRef);
        if (codeDoc.exists) throw new Error(CODE_TAKEN);
      }

      // ---------- FIR LIKHO ----------
      const next = (counterDoc.exists ? counterDoc.data().lastId : 0) + 1;
      campaignCode = formatId(prefix, next);
      campaignRef = db.collection("campaigns").doc(campaignCode); // doc ID == campaignCode

      transaction.set(counterRef, { lastId: next }, { merge: true });

      const finalConfig = buildConfig(type, { ...pricedConfig, code: code || config.code });

      const campaign = {
        campaignCode,
        code, // <-- generic redemption/lookup code, ab HAR type ke liye set hota hai
        type,
        name: name.trim(),
        description: (description || "").trim(),
        status: activateNow ? "active" : "inactive",
        startAt: toTimestamp(startDate),
        endAt: toTimestamp(endDate),
      maxUsesPerCustomer: Number(maxUsesPerCustomer) > 0 ? Number(maxUsesPerCustomer) : 0,
        stackGroup: STACK_GROUP[type],
        eligibility: {
          productIds: eligibility.productIds || [], // one or many eligible products
          packaging: eligibility.packaging ?? null, // pack text when exactly ONE product is eligible e.g. "1 L"
          unit: eligibility.unit || "", // unit when exactly ONE product is eligible e.g. "L", "g"
          packagingOptions: eligibility.packagingOptions || [], // [{ productId, packaging, unit }] for every eligible product
          minDurationDays: Number(eligibility.minDurationDays || 0),
          customerType: eligibility.customerType || "all",
          acquisitionSources: eligibility.acquisitionSources || [],
        },
        config: finalConfig,
        stats: { ...EMPTY_STATS },
        createdBy: localStorage.getItem("userId") || "unknown",
        updatedBy: localStorage.getItem("userId") || "unknown",
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      };

      transaction.set(campaignRef, campaign);

      if (codeRef) {
        transaction.set(codeRef, {
          campaignId: campaignCode,
          campaignType: type,
          active: true,
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
      }
    });

    return campaignCode;
  };

 
  if (adminGivenCode) {
    try {
      const campaignCode = await attemptSave(adminGivenCode);
      return { id: campaignCode, campaignId: campaignCode, couponCode: adminGivenCode };
    } catch (e) {
      if (e.message === CODE_TAKEN) throw new Error(`Coupon code "${adminGivenCode}" pehle se use ho raha hai.`);
      throw e;
    }
  }

 
  for (let attempt = 0; attempt < 5; attempt++) {
    const code = generateCouponCode();
    try {
      const campaignCode = await attemptSave(code);
      return { id: campaignCode, campaignId: campaignCode, couponCode: code };
    } catch (e) {
      if (e.message !== CODE_TAKEN) throw e;
    }
  }
  throw new Error("Code generate nahi ho paya, dobara try karo.");
};

// ---------------------------------------------------------

// ---------------------------------------------------------
export const getAllCampaigns = async (type) => {
  const snapshot = await db
    .collection("campaigns")
    .orderBy("createdAt", "desc")
    .get();

  const list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  return type ? list.filter((c) => c.type === type) : list;
};

// ---------------------------------------------------------

// ---------------------------------------------------------
export const updateCampaignStatus = async (campaignId, newStatus) => {
  await db.collection("campaigns").doc(campaignId).update({
    status: newStatus,
    updatedBy: localStorage.getItem("userId") || "unknown",
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
  });
};

// ---------------------------------------------------------

export const deactivateCouponCode = async (code) => {
  await db.collection("couponCodes").doc(code.toUpperCase()).update({ active: false });
};

// ---------------------------------------------------------

// ---------------------------------------------------------
// products_data price field name is not fixed, so try the usual ones (pack level first, then product level)
const PRICE_KEYS = ["price", "sellingPrice", "salePrice", "offerPrice", "mrp", "unitPrice", "rate", "amount"];
const pickPrice = (pack = {}, prod = {}) => {
  for (const src of [pack, prod]) {
    for (const k of PRICE_KEYS) {
      const v = Number(src?.[k]);
      if (src?.[k] !== undefined && src?.[k] !== null && src?.[k] !== "" && !isNaN(v)) return v;
    }
  }
  return null;
};

export const getProducts = async () => {
  const snapshot = await db.collection("products_data").get();
  if (snapshot.docs[0]) console.log("[products_data] sample doc:", snapshot.docs[0].data());
  return snapshot.docs
    .map((doc) => {
      const d = doc.data();
      const firstPack = d.packagingOptions?.[0] || {};
      return {
        id: d.productId || doc.id,
        name: d.productName || "",
        publishOnApp: d.publishOnApp,
        qty: firstPack.packaging ?? null, // e.g. 1, 200
        unit: firstPack.pkgUnit || "", // e.g. "L", "g"
        price: pickPrice(firstPack, d), // normal price of one pack, from products_data
      };
    })
    .filter((p) => p.name)
    .sort((a, b) => a.name.localeCompare(b.name));
};

// ---------------------------------------------------------

// ---------------------------------------------------------
export const updateCampaign = async (id, input) => {
  const { startDate, endDate, ...rest } = input;
  const data = { ...rest };
  if (startDate) data.startAt = toTimestamp(startDate);
  if (endDate) data.endAt = toTimestamp(endDate);

  // config badla hai to discountAmount / finalPrice dobara nikalo
  if (data.config) {
    const snap = await db.collection("campaigns").doc(id).get();
    const existing = snap.data() || {};
    const productIds = data.eligibility?.productIds || existing.eligibility?.productIds || [];
    data.config = await applyPricing(existing.type, data.config, productIds);
  }

  await db.collection("campaigns").doc(id).update({
    ...data,
    updatedBy: localStorage.getItem("userId") || "unknown",
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
  });
};


// Apply-manually dropdown ke liye: sirf ACTIVE coupon campaigns (date window ke andar),
// jinka applyMode "crm" ya "both" ho (sirf "code" wale coupons CRM me nahi dikhte),
// har coupon ke saath uske product(s) bhi (campaign ke eligibility.productIds -> products_data se naam)
export const getActiveCoupons = async () => {
  const [snap, products] = await Promise.all([
    db.collection("campaigns").where("type", "==", "coupon").where("status", "==", "active").get(),
    getProducts(),
  ]);
  const nameById = new Map(products.map((p) => [String(p.id), p.name]));

  const now = new Date();
  return snap.docs
    .map((doc) => ({ id: doc.id, ...doc.data() }))
    .filter((c) => {
      const start = c.startAt?.toDate ? c.startAt.toDate() : null;
      const end = c.endAt?.toDate ? c.endAt.toDate() : null;
      return (!start || start <= now) && (!end || end >= now);
    })
    // CRM apply: only "crm" / "both" coupons (old coupons with allowAgentApply: true keep working)
    .filter((c) => ["crm", "both"].includes(c.config?.applyMode) || c.config?.allowAgentApply === true)
    .filter((c) => c.code || c.config?.code) // validate API isi redemption code se campaign dhundhta hai
    .map((c) => {
      const productIds = (c.eligibility?.productIds || []).map(String);
      return {
        name: c.name,
        code: c.code || c.config?.code, // /api/coupons/validate ko couponCode ke roop mein jata hai
        campaignCode: c.campaignCode, // e.g. WF-CPN-0001 (sirf dikhane ke liye)
        productIds,
        productNames: productIds.map((id) => nameById.get(id)).filter(Boolean),
      };
    });
};

// Kya ye subscription is coupon ke product ki hai? (coupon par product restriction na ho to sab chalega)
export const subMatchesCoupon = (sub, coupon) => {
  if (!coupon || !coupon.productIds?.length) return true;
  const ids = coupon.productIds.map(String);
  const names = (coupon.productNames || []).map((n) => String(n).trim().toLowerCase());
  const sid = String(sub.product_id ?? sub.productId ?? "");
  const sname = String(sub.product_name ?? sub.productName ?? "").trim().toLowerCase();
  return (!!sid && ids.includes(sid)) || (!!sname && names.includes(sname));
};

// ---------------------------------------------------------
// Customer ke ACTIVE subscriptions (subscriptions_data collection se)
// Fields snake_case mein hain (start_date, subscription_id, status ...)
// ---------------------------------------------------------
// Customer ka field naam pakka nahi pata, isliye sab common naam try karte hain
const CUSTOMER_FIELDS = ["customer_id", "customerId", "customer"];
// status: "1" = active, "0" = paused
const ACTIVE_STATUSES = ["1"];

export const getActiveSubscriptions = async (customerId) => {
  const idVariants = [String(customerId)];
  if (!isNaN(Number(customerId))) idVariants.push(Number(customerId)); // number ke roop mein store ho to bhi mile

  const snaps = await Promise.all(
    CUSTOMER_FIELDS.flatMap((f) =>
      idVariants.map((v) => db.collection("subscriptions_data").where(f, "==", v).get())
    )
  );

  const byId = new Map();
  snaps.forEach((s) => s.docs.forEach((d) => byId.set(d.id, { id: d.id, ...d.data() })));
  const all = [...byId.values()];
  console.log("[subscriptions_data] found:", all.length, all[0]);

  return all.filter((s) => ACTIVE_STATUSES.includes(String(s.status ?? "").toLowerCase()));
};